import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { PageLoading, ErrorState } from "../../components/States";
import { fmtMoney } from "../../lib/format";

const RANGES = [["today", "Today", 0], ["7d", "7 days", 7], ["30d", "30 days", 30], ["90d", "90 days", 90]];

export default function AdminAnalytics() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setData(null); setErr(null);
    const days = RANGES.find(([v]) => v === range)?.[2] ?? 30;
    const end = new Date();
    const start = new Date(end.getTime() - days * 86400000);
    const qs = `start_date=${start.toISOString().slice(0, 10)}&end_date=${end.toISOString().slice(0, 10)}`;
    api.adminAnalytics(qs).then(setData).catch((e) => setErr(e.message));
  }, [range]);

  const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);
  const empty = data && !data.searches && !data.booking_starts && !data.completed_bookings && !data.cancellations;
  const funnel = data ? [["Searches", data.searches], ["Booking started", data.booking_starts], ["Booking completed", data.completed_bookings]] : [];
  const maxFunnel = Math.max(1, ...funnel.map(([, v]) => v));

  return (
    <div data-testid="admin-analytics-page">
      <div className="flex gap-2 flex-wrap" role="tablist" aria-label="Date range">
        {RANGES.map(([v, l]) => (
          <button key={v} role="tab" aria-selected={range === v} data-testid={`analytics-range-${v}`} onClick={() => setRange(v)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${range === v ? "bg-[#E50914] border-[#E50914]" : "bg-[#1F2328] border-[#27272A] text-zinc-300"}`}>{l}</button>
        ))}
      </div>
      {err ? <div className="mt-6"><ErrorState body={err} /></div> : !data ? <div className="mt-6"><PageLoading label="Loading analytics…" /></div> : empty ? (
        <p className="mt-8 text-sm text-zinc-500" data-testid="analytics-empty">No analytics data yet.</p>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              ["Searches", data.searches], ["Booking starts", data.booking_starts],
              ["Completed bookings", data.completed_bookings], ["Cancellations", data.cancellations],
              ["Start conversion", pct(data.booking_start_conversion_rate)],
              ["Completion rate", pct(data.booking_completion_rate)],
              ["Cancellation rate", pct(data.cancellation_rate)],
              ["Avg booking value", data.average_booking_value == null ? "—" : fmtMoney(data.average_booking_value)],
            ].map(([l, v]) => (
              <div key={l} className="card-dark p-5" data-testid={`analytics-stat-${l.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="font-display font-extrabold text-2xl">{v}</p>
                <p className="text-xs text-zinc-400 mt-0.5">{l}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 card-dark p-6 max-w-2xl" data-testid="analytics-funnel">
            <p className="meta-label mb-5">Conversion funnel</p>
            <div className="space-y-4">
              {funnel.map(([l, v], i) => (
                <div key={l}>
                  <div className="flex justify-between text-xs text-zinc-400 mb-1.5"><span>{l}</span><span className="font-semibold text-zinc-200">{v}</span></div>
                  <div className="h-2 rounded-full bg-[#080B0D] overflow-hidden">
                    <div className="h-full rounded-full bg-[#E50914] transition-all duration-500" style={{ width: `${(v / maxFunnel) * 100}%` }} />
                  </div>
                  {i < funnel.length - 1 && <p className="text-center text-zinc-700 text-xs mt-2">↓</p>}
                </div>
              ))}
            </div>
            <p className="mt-5 text-[11px] text-zinc-600 leading-relaxed">Cancellations ({data.cancellations}) are tracked separately from the funnel. Average booking value is the gross completed-booking total (incl. service fee), USD. Event collection began at deployment — no historical events were fabricated.</p>
          </div>
        </>
      )}
    </div>
  );
}
